import { saveAs } from "file-saver";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";

const parseDatePayload = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return { date: d, month: m, year: y };
};


function normalizeApiRow(row) {
  const normalized = {};

  let currentShelf = null;
  const shelfHeaderRegex = /^Shelf\s+(\d+)\s*-\s*(.+)$/;

  Object.entries(row).forEach(([key, value]) => {
    const m = key.match(shelfHeaderRegex);

    if (m) {
      const shelfNum = Number(m[1]);
      const subKey = m[2].trim();
      currentShelf = shelfNum;
      if (SHELF_SUB_COLS.includes(subKey)) {
        normalized[`Shelf ${shelfNum} - ${subKey}`] = value;
      }
      return;
    }

    if (currentShelf !== null && SHELF_SUB_COLS.includes(key)) {
      normalized[`Shelf ${currentShelf} - ${key}`] = value;
      return;
    }

    if (currentShelf === null && NON_SHELF_HEADERS.includes(key)) {
      normalized[key] = value;
      return;
    }

  });

  return normalized;
}

// ── Non-shelf columns  ──────────────────────
const NON_SHELF_HEADERS = [
  "Month",
  "Year",
  "Customer Code",
  "Shop ID",
  "Shop Name",
  "Outlet Type",
  "Outlet Class",
  "Town Code",
  "Town Name",
  "Outlet Address",
  "Region",
   "Market",
   "RSM Name",
   "ASM Name",
   "SO Name",
   "Sales Manager Name",
   "MDC Code",
   "DISTRIBUTOR Name",
   "No of Shelves",
  "Cooler Location (Q7a)", 
  "Cooler Type (Q7f)",
  "FootFalldriversCount",
  "TransactionDriversCount",
];

// ── Sub-columns repeated for every shelf ───────────
const SHELF_SUB_COLS = [
  "Foreign Product Present",
  "Planogram",
  "Q10.1 Coke 250 ml RGB",
  "Q10.2 Sprite 250 ml  RGB",
  "Q10.3 Fanta 250 ml  RGB",
  "Q10.5 Kinley Soda 250 ml  RGB",
  "Q10.6 Coke 175 ml RGB",
  "Q10.7 Sprite 175 ml RGB",
  "Q10.8 Fanta 175 ml RGB",
  "Q10.9 Coke 250 ml PET",
  "Q10.10 Sprite 250 ml PET",
  "Q10.11 Fanta Orange 250 ml PET",
  "Q10.12 Fanta Lemon 250 ml PET",
  "Q10.13 Kinley Soda",
  "Q12a.1 Coke ASSP 500ml",
  "Q12a.2 Sprite ASSP 500ml",
  "Q12a.3 Fanta Orange  ASSP 500ml",
  "Q12a.4 Coke Zero",
  "Q12a.6  Kinley Soda 500/600/750ml",
  "Q12a.7 Kinley Water 500/600/750ml",
  "Q13.1 Coke 1000 ML",
  "Q13.2 Sprite 1000 ML",
  "Q13.3 Fanta 1000 ML",
  "Q13.4 Kinley Water 1000 ML",
  "Q14.1 Coke FC1500",
  "Q14.2 Sprite FC1500",
  "Q14.3 Fanta FC1500",
  "Q15.1 Coke FC2000",
  "Q15.2 Sprite FC2000",
  "Q15.3 Fanta Orange FC2000",
  "Q15.4 Fanta Lemon FC2000",
  "Q16.1 Coke FC2250",
  "Q16.2 Sprite FC2250",
  "Q16.3 Fanta Orange FC2250",
  "Q16.4 Fanta Lemon FC2250",
];


const SHELF_ORDER = [1,  2, 3, 4, 5, 6, 7, 8, 9,10];


const ALL_HEADERS = [
  ...NON_SHELF_HEADERS,
  ...SHELF_ORDER.flatMap((n) => SHELF_SUB_COLS.map((sub) => `Shelf ${n} - ${sub}`)),
];

export async function handleManualStoreAuditExport(from, to, setLoading) {
  setLoading(true);
  try {
    const body = { from: parseDatePayload(from), to: parseDatePayload(to) };
    const res  = await apiClient.post(`${API_CONFIG.ENDPOINTS.REPORT_CENTER.MANUAL_STORE_AUDIT}`, body);
    if (res.status !== 200) throw new Error("API failed: " + res.status);

    const list = Array.isArray(res.data) ? res.data : [res.data];
    if (!list.length) throw new Error("No data returned from API");


    const redHeaderStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "C72127" } },
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const orangeHeaderStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "CC5500" } },
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const dataStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
      font: { color: { rgb: "2D3748" }, sz: 11 },
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const headerRow1 = [];
    const headerRow2 = [];
    const merges     = [];


    NON_SHELF_HEADERS.forEach((h, i) => {
      headerRow1.push({ v: h, t: "s", s: redHeaderStyle });
      headerRow2.push({ v: "",  t: "s", s: redHeaderStyle });
      merges.push({ s: { r: 0, c: i }, e: { r: 1, c: i } });
    });

   
    SHELF_ORDER.forEach((n, shelfIdx) => {
      const startCol = NON_SHELF_HEADERS.length + shelfIdx * SHELF_SUB_COLS.length;
      const endCol   = startCol + SHELF_SUB_COLS.length - 1;

      headerRow1.push({ v: `Shelf ${n}`, t: "s", s: orangeHeaderStyle });
      for (let i = 1; i < SHELF_SUB_COLS.length; i++) {
        headerRow1.push({ v: "", t: "s", s: orangeHeaderStyle });
      }
      merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: endCol } });

   
      SHELF_SUB_COLS.forEach((sub) => {
        headerRow2.push({ v: sub, t: "s", s: redHeaderStyle });
      });
    });

  
    const dataRows = list.map((row) => {
    const normalized = normalizeApiRow(row);
    return ALL_HEADERS.map((h) => ({
      v: normalized[h] !== undefined && normalized[h] !== null ? String(normalized[h]) : "0",
      t: "s",
      s: dataStyle,
    }));
  });

    const wsData = [headerRow1, headerRow2, ...dataRows];

    const XLSX = await import("xlsx-js-style");
    const ws   = XLSX.utils.aoa_to_sheet(wsData);
    ws["!merges"] = merges;
    ws["!cols"]   = ALL_HEADERS.map(() => ({ wch: 22 }));
    ws["!rows"]   = [{ hpt: 40 }, { hpt: 40 }, ...list.map(() => ({ hpt: 22 }))];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Manual Store Audit");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `manual_store_audit_${from}_to_${to}.xlsx`
    );
  } catch (err) {
    console.error("Manual Store Audit export error:", err);
    alert("Failed to generate Excel: " + (err.message || err));
  } finally {
    setLoading(false);
  }
}